import { formatDate } from '../utils/dateConverter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CallOutcomeModal from '../modals/CallOutcomeModal';
import LeadModal from '../modals/LeadModal';
import {
    addLead,
    convertLead,
    deleteLead,
    getLeads,
    patchLead,
} from '../services/LeadService';
import { LEAD_SOURCES, leadSourceLabel } from '../utils/salesPipeline';
import { isoInDays, leadToCallItem } from '../utils/callList';
import { extractApiError } from '../utils/apiError';
import { useConfirm, useToast } from '../context/UIContext';
import '../styles/Leads.css';

const STATUSES = [
    { key: 'open',        label: 'Offen' },
    { key: 'contacted',   label: 'Kontaktiert' },
    { key: 'no_interest', label: 'Kein Interesse' },
    { key: 'converted',   label: 'Zu Deal' },
];

const statusLabel = (key) => STATUSES.find(s => s.key === key)?.label ?? key;

const fmtDate = (value) => (value ? formatDate(value) : '—');

const EMPTY_QUICK = { companyName: '', contactPerson: '', phone: '' };

export default function Leads() {
    const toast = useToast();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [onlyDue, setOnlyDue] = useState(false);
    const [scope, setScope] = useState('all');
    const [quick, setQuick] = useState(EMPTY_QUICK);
    const [saving, setSaving] = useState(false);
    const [callItem, setCallItem] = useState(null);
    const [editLead, setEditLead] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        // Closed leads are fetched too; the status filter decides what is shown.
        return getLeads({ scope, includeClosed: true })
            .then(data => { setLeads(data); setLoadError(''); })
            .catch(() => setLoadError('Leadliste konnte nicht geladen werden. Bitte versuche es erneut.'))
            .finally(() => setLoading(false));
    }, [scope]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => leads.filter(lead => {
        const term = search.trim().toLowerCase();
        const matchSearch = !term
            || (lead.companyName || '').toLowerCase().includes(term)
            || (lead.contactPerson || '').toLowerCase().includes(term)
            || (lead.city || '').toLowerCase().includes(term)
            || (lead.industry || '').toLowerCase().includes(term)
            || (lead.phone || '').toLowerCase().includes(term);
        const matchStatus = statusFilter
            ? lead.status === statusFilter
            : ['open', 'contacted'].includes(lead.status);
        const matchDue = !onlyDue || Boolean(leadToCallItem(lead));
        return matchSearch && matchStatus && matchDue;
    }), [leads, search, statusFilter, onlyDue]);

    const counts = useMemo(() => ({
        open: leads.filter(l => l.status === 'open').length,
        contacted: leads.filter(l => l.status === 'contacted').length,
        due: leads.filter(l => Boolean(leadToCallItem(l))).length,
        converted: leads.filter(l => l.status === 'converted').length,
    }), [leads]);

    const handleQuickAdd = (e) => {
        e.preventDefault();
        if (!quick.companyName.trim()) return;
        setSaving(true);
        // No date given → the backend puts it on today's call list.
        addLead(quick)
            .then(() => { setQuick(EMPTY_QUICK); return load(); })
            .catch(err => toast(extractApiError(err, 'Lead konnte nicht angelegt werden.')))
            .finally(() => setSaving(false));
    };

    const handleSchedule = (lead, days) => {
        patchLead(lead.id, { next_follow_up_date: isoInDays(days) })
            .then(load)
            .catch(err => toast(extractApiError(err, 'Termin konnte nicht gesetzt werden.')));
    };

    const handleConvert = async (lead) => {
        const ok = await confirm(
            `"${lead.companyName}" in einen Deal umwandeln? Der Lead verlässt danach die Anrufliste.`,
            { confirmLabel: 'Deal anlegen' }
        );
        if (!ok) return;
        convertLead(lead.id)
            .then(res => {
                toast('Deal angelegt.', 'success');
                navigate(`/project_acquisition?deal=${res.acquisition_id}`);
            })
            .catch(err => toast(extractApiError(err, 'Umwandeln fehlgeschlagen.')));
    };

    const handleDelete = async (lead) => {
        const ok = await confirm(`"${lead.companyName}" wirklich aus der Leadliste löschen?`, {
            confirmLabel: 'Löschen', danger: true,
        });
        if (!ok) return;
        deleteLead(lead.id)
            .then(load)
            .catch(err => toast(extractApiError(err, 'Löschen fehlgeschlagen.')));
    };

    return (
        <div className="leads-page">
            {loadError && (
                <div className="alert alert--error" style={{ marginBottom: 16 }}>
                    {loadError}
                    <button className="alert__close" onClick={load} style={{ marginLeft: 12 }}>
                        Erneut versuchen
                    </button>
                </div>
            )}

            <div className="leads-hero">
                <div>
                    <h2 className="leads-hero-title">Leadliste</h2>
                    <p className="leads-hero-sub">
                        Firmen, die angerufen werden sollen — vor dem Deal. Fällige Leads stehen auf der
                        Anrufliste im Vertrieb.
                    </p>
                </div>
                <div className="leads-counts">
                    <span><strong>{counts.open}</strong> offen</span>
                    <span><strong>{counts.contacted}</strong> kontaktiert</span>
                    <span className={counts.due ? 'is-due' : ''}><strong>{counts.due}</strong> heute fällig</span>
                    <span><strong>{counts.converted}</strong> zu Deals</span>
                </div>
            </div>

            {/* Quick add — a target list is only useful if writing one down takes seconds */}
            <form className="leads-quickadd" onSubmit={handleQuickAdd}>
                <input
                    type="text"
                    className="input"
                    placeholder="Firma *"
                    value={quick.companyName}
                    onChange={e => setQuick(p => ({ ...p, companyName: e.target.value }))}
                />
                <input
                    type="text"
                    className="input"
                    placeholder="Ansprechpartner"
                    value={quick.contactPerson}
                    onChange={e => setQuick(p => ({ ...p, contactPerson: e.target.value }))}
                />
                <input
                    type="tel"
                    className="input"
                    placeholder="Telefon"
                    value={quick.phone}
                    onChange={e => setQuick(p => ({ ...p, phone: e.target.value }))}
                />
                <button className="button" type="submit" disabled={saving || !quick.companyName.trim()}>
                    + Hinzufügen
                </button>
                <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => { setEditLead(null); setShowModal(true); }}
                >
                    Mit Details…
                </button>
            </form>

            <div className="leads-toolbar">
                <input
                    type="text"
                    className="input_filter"
                    placeholder="Firma, Kontakt, Ort oder Nummer suchen…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">Offen &amp; kontaktiert</option>
                    {STATUSES.map(s => <option key={s.key} value={s.key}>{statusLabel(s.key)}</option>)}
                </select>
                <label className="acq-checkbox">
                    <input type="checkbox" checked={onlyDue} onChange={e => setOnlyDue(e.target.checked)} />
                    Nur heute fällige
                </label>
                <div className="view-toggle">
                    <button
                        className={`view-btn ${scope === 'mine' ? 'view-btn-active' : ''}`}
                        onClick={() => setScope('mine')}
                    >
                        Meine
                    </button>
                    <button
                        className={`view-btn ${scope === 'all' ? 'view-btn-active' : ''}`}
                        onClick={() => setScope('all')}
                    >
                        Alle
                    </button>
                </div>
            </div>

            <div className="leads-table-wrap">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Firma</th>
                            <th>Ansprechpartner</th>
                            <th>Telefon</th>
                            <th>Ort / Branche</th>
                            <th>Quelle</th>
                            <th>Status</th>
                            <th>Versuche</th>
                            <th>Wiedervorlage</th>
                            <th>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(lead => {
                            const due = leadToCallItem(lead);
                            return (
                                <tr key={lead.id} className={lead.status === 'no_interest' ? 'leads-row-closed' : ''}>
                                    <td>
                                        <span className="leads-company">{lead.companyName}</span>
                                        {lead.notes && (
                                            <span className="leads-note" title={lead.notes}>
                                                {lead.notes.split('\n')[0]}
                                            </span>
                                        )}
                                    </td>
                                    <td>{lead.contactPerson || '—'}</td>
                                    <td>
                                        {lead.phone
                                            ? <a className="leads-phone" href={`tel:${lead.phone}`}>{lead.phone}</a>
                                            : '—'}
                                    </td>
                                    <td className="leads-td-muted">
                                        {[lead.city, lead.industry].filter(Boolean).join(' · ') || '—'}
                                    </td>
                                    <td className="leads-td-muted">{leadSourceLabel(lead.source)}</td>
                                    <td>
                                        <span className={`leads-status is-${lead.status}`}>
                                            {statusLabel(lead.status)}
                                        </span>
                                    </td>
                                    <td>
                                        {lead.contactAttempts || 0}
                                        {lead.lastContactDate && (
                                            <span className="leads-last" title="Letzter Kontakt">
                                                {fmtDate(lead.lastContactDate)}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`leads-followup${due ? ` is-${due.bucket}` : ''}`}>
                                            {fmtDate(lead.nextFollowUpDate)}
                                        </span>
                                    </td>
                                    <td className="leads-actions">
                                        {['open', 'contacted'].includes(lead.status) && (
                                            <>
                                                <button
                                                    className="button"
                                                    onClick={() => setCallItem(
                                                        leadToCallItem(lead) || {
                                                            kind: 'lead',
                                                            id: lead.id,
                                                            company: lead.companyName,
                                                            contact: lead.contactPerson || '',
                                                            phone: lead.phone || '',
                                                            stage: lead.status,
                                                        }
                                                    )}
                                                >
                                                    Anruf
                                                </button>
                                                <button
                                                    className="button button-secondary"
                                                    onClick={() => handleSchedule(lead, 0)}
                                                    title="Auf die heutige Anrufliste setzen"
                                                >
                                                    Heute
                                                </button>
                                                <button
                                                    className="button button-secondary"
                                                    onClick={() => handleConvert(lead)}
                                                    title="Lead in einen Deal umwandeln"
                                                >
                                                    → Deal
                                                </button>
                                            </>
                                        )}
                                        {lead.convertedAcquisitionId && (
                                            <button
                                                className="button button-secondary"
                                                onClick={() => navigate(`/project_acquisition?deal=${lead.convertedAcquisitionId}`)}
                                            >
                                                Deal öffnen
                                            </button>
                                        )}
                                        <button
                                            className="button-warning"
                                            onClick={() => { setEditLead(lead); setShowModal(true); }}
                                        >
                                            Bearbeiten
                                        </button>
                                        <button className="button-danger" onClick={() => handleDelete(lead)}>
                                            Löschen
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={9} className="leads-empty">
                                    {loading
                                        ? 'Daten werden geladen…'
                                        : leads.length === 0
                                            ? 'Noch keine Leads. Trag die erste Firma oben ein — sie steht sofort auf der heutigen Anrufliste.'
                                            : 'Keine Leads für diese Filter.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {callItem && (
                <CallOutcomeModal
                    item={callItem}
                    onClose={() => setCallItem(null)}
                    onSaved={() => { setCallItem(null); load(); }}
                />
            )}

            {showModal && (
                <LeadModal
                    lead={editLead}
                    sources={LEAD_SOURCES}
                    onClose={() => { setShowModal(false); setEditLead(null); }}
                    onSave={() => { setShowModal(false); setEditLead(null); load(); }}
                />
            )}
        </div>
    );
}
